import { observer } from "mobx-react";
import * as React from "react";
import styled, { css } from "styled-components";

import Button from "@client/components/ui/Button";
import ButtonLoading from "@client/components/ui/ButtonLoading";
import InputField, { Label, LabelText } from "@client/components/ui/InputField";
import Switch from "@client/components/ui/Switch";
import Style from "@client/utils/Style";
import { InputField as InputFieldType } from "@typings/InputField";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;
const Header = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0;
`;
const LicenceImage = styled.div<{ image: string }>`
  ${Style.exactSize("96px")}
  background: url('${(props: any) => props.image}') center no-repeat;
`;
const FieldGroups = styled.div`
  display: flex;
  flex-direction: column;
`;
const Fields = styled.div<{ groupName: string }>`
  display: flex;
  flex-wrap: wrap;
  margin: 0 32px;
  justify-content: space-between;
  > div {
    margin-top: 16px;
  }
  &:first-child {
    margin-top: -16px;
  }
  &:last-child {
    margin-top: -8px;
  }
`;
const VersionWrapper = styled.div``;
const VersionOptions = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  ${props =>
    props.disabled
      ? css`
          color: #00000059;
        `
      : ""}
`;
const VersionOption = styled.div`
  font-size: 12px;
  &:first-child {
    margin-right: 8px;
  }
  &:last-child {
    margin-left: 8px;
  }
`;
const SegmentedControl = styled.div<{ disabled?: boolean }>`
  display: flex;
  ${Style.exactWidth(`${Style.inputSizes.large.width}px`)}
  ${Style.exactHeight(`${Style.inputSizes.large.height}px`)}
  border: 1px solid ${Style.palette.grayscale[2]};
  border-radius: ${Style.borderRadius};
  overflow: hidden;
  ${props =>
    props.disabled
      ? css`
          opacity: 0.5;
          pointer-events: none;
        `
      : ""}
`;
const Segment = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-size: 12px;
  cursor: pointer;
  transition: all ${Style.animations.swift};
  background: ${props =>
    props.selected ? Style.palette.primary : "transparent"};
  color: ${props => (props.selected ? "white" : Style.palette.black)};
  font-weight: ${props =>
    props.selected ? Style.fontWeights.medium : Style.fontWeights.regular};
  & + & {
    border-left: 1px solid ${Style.palette.grayscale[2]};
  }
`;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 48px 32px 32px 32px;
  flex-grow: 1;
  align-items: flex-end;
`;
const TagItem = styled.div`
  word-break: break-word;
  max-width: 100%;
  display: flex;
  align-items: center;
`;
const TagBullet = styled.div<{ color?: string | null }>`
  ${Style.exactSize("8px")}
  border: 1px solid white;
  background: ${props => props.color || "#777A8B"};
  border-radius: 50%;
`;
const TagLabel = styled.div`
  margin-left: 8px;
`;

type Props = {
  inputFields: InputFieldType[];
  highlightedFields: string[];
  loading: boolean;
  licenceImage: any;
  hideTypeToggle?: boolean;
  onFieldChange: (field: InputFieldType, value: any) => void;
  onNewCustomerNameChange: (name: string) => void;
  onRequestClose: () => void;
  onGenerateClick: () => void;
};

@observer
class LicenceModalInputForm extends React.Component<Props> {
  renderTagItem(enumItem: {
    label: string;
    value: string;
    color?: string | null;
  }) {
    return (
      <TagItem>
        <TagBullet color={enumItem.color || undefined} />
        <TagLabel>{enumItem.label}</TagLabel>
      </TagItem>
    );
  }

  renderTagButton() {
    const tagField = this.props.inputFields.find(f => f.name === "tag");
    if (!tagField || !tagField.enum) {
      return null;
    }
    const tagData = tagField.enum.find(t => t.value === tagField!.value);
    if (!tagData) {
      return null;
    }
    return (
      <TagItem>
        <TagBullet color={tagData.color} />
        <TagLabel>{tagData.label}</TagLabel>
      </TagItem>
    );
  }

  renderFields() {
    const licenceTypeIndex = this.props.inputFields.findIndex(
      f => f.name === "type",
    );
    const replicasIndex = this.props.inputFields.findIndex(
      f => f.name === "replicas",
    );
    const migrationsIndex = this.props.inputFields.findIndex(
      f => f.name === "migrations",
    );
    const versionIndex = this.props.inputFields.findIndex(
      f => f.name === "version",
    );
    const groups = [
      this.props.inputFields.filter((_, i) => i < replicasIndex),
      [
        this.props.inputFields[replicasIndex],
        this.props.inputFields[migrationsIndex],
      ],
      [
        this.props.inputFields[versionIndex],
        this.props.inputFields[licenceTypeIndex],
      ],
      this.props.inputFields.filter((_, i) => i > licenceTypeIndex),
    ];
    const currentLicenceType = this.props.inputFields[licenceTypeIndex].value
      ? "floating"
      : "fixed";

    return (
      <FieldGroups>
        {groups.map((group, i) => (
          <Fields key={i} groupName={group[0].name}>
            {group.map(field => {
              const usableField = { ...field };
              if (
                field.name === "customer_id" &&
                field.enum &&
                field.enum.length < 5
              ) {
                usableField.enumAllowNew = true;
                usableField.enum = [
                  ...usableField.enum!,
                  { label: "Add new...", value: "__addnew__" },
                ];
              }
              if (field.name === "customer_email") {
                const orgField = this.props.inputFields.find(
                  f => f.name === "customer_id",
                );
                if (!orgField || orgField.value !== "__addnew__") {
                  return null;
                }
              }
              if (
                field.name === "start_date" &&
                currentLicenceType === "floating"
              ) {
                return null;
              }
              if (field.name === "duration" && currentLicenceType === "fixed") {
                return null;
              }
              if (field.hidden) {
                return null;
              }
              if (field.name === "version") {
                return (
                  <VersionWrapper key="version">
                    <Label>
                      <LabelText>{field.label}</LabelText>
                    </Label>
                    <SegmentedControl
                      disabled={this.props.loading || field.disabled}
                    >
                      {(field.enum || []).map(option => (
                        <Segment
                          key={option.value}
                          selected={field.value === option.value}
                          onClick={() => {
                            this.props.onFieldChange(field, option.value);
                          }}
                        >
                          {option.label}
                        </Segment>
                      ))}
                    </SegmentedControl>
                  </VersionWrapper>
                );
              }
              if (field.name === "type") {
                if (this.props.hideTypeToggle) {
                  return null;
                }
                return (
                  <VersionWrapper key="type">
                    <Label>
                      <LabelText>Licence Type</LabelText>
                    </Label>
                    <VersionOptions>
                      <VersionOption>Fixed</VersionOption>
                      <Switch
                        big
                        disabled={field.disabled}
                        checked={field.value}
                        onChange={value => {
                          this.props.onFieldChange(field, value);
                        }}
                      />
                      <VersionOption>Floating</VersionOption>
                    </VersionOptions>
                  </VersionWrapper>
                );
              }

              const isValidDate = (field: InputFieldType, date: Date) => {
                if (field.name !== "end_date") {
                  return true;
                }
                if (currentLicenceType === "floating") {
                  return true;
                }
                const startDate = this.props.inputFields.find(
                  f => f.name === "start_date",
                );
                const startDateValue = startDate && startDate.value;
                if (!startDateValue) {
                  return true;
                }
                return (
                  new Date(date).getTime() > new Date(startDateValue).getTime()
                );
              };

              return (
                <InputField
                  key={field.name}
                  field={usableField}
                  highlight={
                    !!this.props.highlightedFields.find(
                      name => field.name === name,
                    )
                  }
                  enumItemRender={
                    field.name === "tag"
                      ? item => this.renderTagItem(item)
                      : null
                  }
                  selectedItemComponent={
                    field.name === "tag" ? this.renderTagButton() : null
                  }
                  onChange={value => {
                    this.props.onFieldChange(field, value);
                  }}
                  onEnumNewValueChange={value => {
                    this.props.onNewCustomerNameChange(value);
                  }}
                  disabled={this.props.loading || field.disabled}
                  isValidDate={isValidDate}
                />
              );
            })}
          </Fields>
        ))}
      </FieldGroups>
    );
  }

  renderButtons() {
    return (
      <Buttons>
        <Button
          secondary
          large
          onClick={this.props.onRequestClose}
          disabled={this.props.loading}
        >
          Cancel
        </Button>
        {this.props.loading ? (
          <ButtonLoading large>Generating...</ButtonLoading>
        ) : (
          <Button
            large
            onClick={() => {
              this.props.onGenerateClick();
            }}
            disabled={this.props.loading}
          >
            Generate
          </Button>
        )}
      </Buttons>
    );
  }

  render() {
    return (
      <Wrapper>
        <Header>
          <LicenceImage image={this.props.licenceImage} />
        </Header>
        {this.renderFields()}
        {this.renderButtons()}
      </Wrapper>
    );
  }
}

export default LicenceModalInputForm;
